"use client";

import { useState } from "react";
import Link from "next/link";
import { Menu } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { useI18n } from "@/lib/i18n/hook";
import { useAuth } from "./auth-context";
import LanguageSelector from "./language-selector";

// Types
interface NavLink {
  href: string;
  label: string;
  requiresAuth?: boolean;
}

// Constants
const NAV_LINKS: NavLink[] = [
  { href: "/", label: "home" },
  { href: "/life-plan", label: "lifePlan" },
  { href: "/risk-simulation", label: "riskSimulation" },
  { href: "/mypage", label: "mypage", requiresAuth: true },
  { href: "/contact", label: "contact" },
];

// Components
function Logo() {
  const { t } = useI18n();
  return (
    <Link href="/" className="flex items-center">
      <svg
        width="64"
        height="64"
        viewBox="0 0 200 200"
        xmlns="http://www.w3.org/2000/svg"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        className="h-8 w-8 ml-2 mr-2"
      >
        <path d="M 100 150 m -90,0 a 90,90 0 1,1 180,0 a 70,70 0 1,0 -140,0 a 50,50 0 1,1 100,0" />
      </svg>
      <span className="font-bold text-xl ml-2">{t("appTitle")}</span>
    </Link>
  );
}

function DesktopNav() {
  const { t } = useI18n();
  const { isLoggedIn } = useAuth();

  return (
    <nav className="hidden md:flex items-center gap-6">
      {NAV_LINKS.map((link) => {
        if (link.requiresAuth && !isLoggedIn) return null;
        return (
          <Link
            key={link.href}
            href={link.href}
            className="text-sm font-medium transition-colors hover:text-primary"
          >
            {t(`navigation.${link.label}`)}
          </Link>
        );
      })}
      <LanguageSelector />
    </nav>
  );
}

function MobileNav({
  isOpen,
  setIsOpen,
}: {
  isOpen: boolean;
  setIsOpen: (open: boolean) => void;
}) {
  const { t } = useI18n();
  const { isLoggedIn } = useAuth();

  return (
    <Sheet open={isOpen} onOpenChange={setIsOpen}>
      <SheetTrigger asChild className="md:hidden">
        <Button variant="outline" size="icon">
          <Menu className="h-5 w-5" />
          <span className="sr-only">{t("navigation.openMenu")}</span>
        </Button>
      </SheetTrigger>
      <SheetContent side="right">
        <nav className="flex flex-col gap-4 mt-8">
          {NAV_LINKS.map((link) => {
            if (link.requiresAuth && !isLoggedIn) return null;
            return (
              <Link
                key={link.href}
                href={link.href}
                className="text-base font-medium transition-colors hover:text-primary"
                onClick={() => setIsOpen(false)}
              >
                {t(`navigation.${link.label}`)}
              </Link>
            );
          })}
          <div className="flex items-center gap-4 mt-4">
            <LanguageSelector />
          </div>
        </nav>
      </SheetContent>
    </Sheet>
  );
}

// Main Component
export function Header() {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <header className="sticky top-0 z-50 w-full border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
      <div className="flex h-16 items-center justify-between px-4 md:px-6 lg:px-8">
        <div className="flex items-center gap-2">
          <Logo />
        </div>
        <DesktopNav />
        <MobileNav isOpen={isOpen} setIsOpen={setIsOpen} />
      </div>
    </header>
  );
}
